import { FirebaseOptions } from "firebase/app";
import { AppEnvironment, getAppEnvironment } from "src/config/appEnvironment.ts";
import deepMerge from "src/utils/deepMerge.ts";

export type AppConfig = {
	firebase: {
		firebaseOptions: FirebaseOptions;
		vapidKey: string;
	};
	sentry: SentryConfig;
	googleMaps: {
		browserApiKey: string;
	};
};

export type SentryConfig = {
	dsn: string | undefined;
	showErrorReportForUncaughtExceptions?: boolean;
	replaysSessionSampleRate?: number;
	debug?: boolean;
	environment?: string;
	tracesSampleRate?: number;
};

export const getFirebaseOptions = (appEnvironment?: AppEnvironment): FirebaseOptions =>
	getConfig(appEnvironment).firebase.firebaseOptions;

export const getFirebaseVapidKey = () => getConfig().firebase.vapidKey;

export const getSentryConfig = (): SentryConfig | undefined => {
	return getConfig().sentry;
};

export const getFrontendReleaseCode = () => {
	const appEnvironment = getAppEnvironment();
	if (appEnvironment === "dev") return "dev";
	// Read from window global instead of import.meta.env to avoid hash cascade problem.
	// The value is injected into index.html by injectReleaseCodePlugin in vite.config.js.
	return window.AAVO_RELEASE_CODE ?? "unknown";
};

export const getGoogleMapsBrowserApiKey = () => getConfig().googleMaps?.browserApiKey ?? "";

const getConfig = (appEnvironment?: AppEnvironment): any => {
	switch (appEnvironment ?? getAppEnvironment()) {
		case "dev":
			return getDevConfig();
		case "staging":
			return getStagingConfig();
		case "prod":
			return getProdConfig();
	}
};

const getDevConfig = (): AppConfig => {
	const sentryConfig =
		import.meta.env.VITE_USE_SENTRY_ON_DEV === "true" ?
			{
				dsn: "https://249b53a48ebb618724bf69450cfed38e@o4511573174976512.ingest.de.sentry.io/4511573302640720",
				showErrorReportForUncaughtExceptions: true,
				tracesSampleRate: 1.0,
				replaysSessionSampleRate: 0,
				debug: false,
				environment: "dev",
			}
		:	{
				dsn: undefined,
			};

	return {
		firebase: {
			firebaseOptions: {
				apiKey: "AIzaSyC_oxjm4Wq9fBTEkQBBGpGOhWIWmLrRNVA",
				authDomain: "aavo-dev.firebaseapp.com",
				projectId: "aavo-dev",
				storageBucket: "aavo-dev.appspot.com",
				messagingSenderId: "717179742357",
				appId: "1:717179742357:web:e8c063470a169386fd939c",
			},
			vapidKey: "BNl6XpoBPa1lgWw4p3szpXGltWTEQft1AjLY1fw1F5AT-XUVCWrXARUUD7sDswn-znqW28gX1Vn7DhPZZFpFBcA",
		},
		sentry: sentryConfig,
		googleMaps: {
			browserApiKey: import.meta.env.VITE_GOOGLE_MAPS_BROWSER_API_KEY ?? "",
		},
	};
};

const getStagingConfig = (): AppConfig => {
	const prodConfig = getProdConfig();
	return deepMerge(prodConfig, {
		sentry: {
			environment: "staging",
		},
	});
};

const getProdConfig = (): AppConfig => {
	const configJson = import.meta.env.VITE_APP_CONFIG;
	if (!configJson) throw Error("Production configuration is not defined");

	return JSON.parse(configJson);
};
